import React, { useEffect, useRef, useState } from "react";
import {
    CircularProgress,
    Chip,
    Drawer,
    Dropdown,
    ListItemDecorator,
    Menu,
    MenuButton,
    MenuItem,
    ModalClose,
    Switch,
    Textarea,
    Tooltip
} from "@mui/joy";
import Box from "@mui/joy/Box";
import Input from "@mui/joy/Input";
import Typography from "@mui/joy/Typography";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import { Add, ArrowDropDown, DeleteForever, SolarPowerOutlined } from "@mui/icons-material";
import 'react-datepicker/dist/react-datepicker.css';
import Button from "@mui/joy/Button";
import IconButton from "@mui/joy/IconButton";
import Tabs from '@mui/joy/Tabs';
import TabList from '@mui/joy/TabList';
import Tab from '@mui/joy/Tab';
import TabPanel from '@mui/joy/TabPanel';
import { toast, ToastContainer } from "react-toastify";

import ApiService, { getServicePrices, prepareLeadData } from "../../../axios/Bookings-Axios";
import { GetUser, GetFranchiseVans } from "../../../axios/Axios";
import BookingsDrawer from "../BookingsDrawer";
import ConfirmDeleteModal from "../ConfirmDeleteModal";
import CustomersTopComponent from "../CustomersTopComponent";
import { INITIAL_SERVICES, ServiceState as AppServiceState } from "../../../domain/Services";
import ConfirmCustomersRoofTab, {
    ConfirmCustomersRoofTabHandle,
    PreBookingAnswers,
    RoofPinConfirmationState,
    createEmptyPreBookingAnswers,
    validatePreBookingAnswers,
    composeBookingNotes,
    preBookingAnswersToPayload,
    preBookingAnswersFromLead,
    getBookingBlockMessage,
} from "./ConfirmCustomersRoofTab";
import DrawerWithStickyFooter from "../../dashboard-components/DrawerWithStickyFooter";
import LeadActivityFlow from "../../flows/LeadActivityFlow";
// 1. Import the new modal component
import CreateCallbackReminderModal from "../../modals/CreateCallbackReminderModal";
import CustomerMessagesTab from "../../dashboard-pages/messages/CustomerMessagesTab";
import FranchiseeContactCard from "../FranchiseeContactCard";

interface Customer {
    advertisement_platform?: string;
    created_at: string;
    email: string;
    full_name: string;
    house_type: { name: string, id: number };
    id: number;
    notes: string;
    phone: string;
    postcode: string;
    street_address: string;
    roof: {
        id: number;
        confirmed: boolean;
        square_meter: number;
        roof_url: string;
        face_count: number;
        pin_latitude?: number | null;
        pin_longitude?: number | null;
        pin_confirmed_at?: string | null;
    };
    has_conservatory: boolean;
    has_detached_garage: boolean;
    has_attached_garage: boolean;
    franchise_postcode?: {
        franchise: { 
            id: number;
            location: string;
            owner?: string;
            phone?: string;
            email?: string;
        };
        affluence_multiplier?: number;
        service_price_tier?: any;
    };
}

interface Lead {
    created_at: string;
    customer: Customer;
    id: number;
    notes?: string;
    paid: boolean;
    services: Array<{ name: string } | string>;
    service_price: Array<{ service: { name: string } | string, price: number }>;
    status: any[];
    solar_panels: number;
    decking_si?: number;
    patio_si?: number;
    driveway_si?: number;
    days_to_complete: number;
    sent_roof_quote?: boolean;
    quote_status?: 'preparing' | 'ready' | 'failed';
    ai_responses_enabled?: boolean;
    is_referred?: boolean;
    referral_badge_label?: string;
    referred_by_name?: string | null;
    referral_discount_percent?: number;
}

function formatDate(old_date: string | Date) {
    let date = new Date(old_date);
    if (!isNaN(date.getTime())) {
        return date.toLocaleString('en-US', {
            weekday: 'long', year: 'numeric', month: 'long',
            day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric'
        });
    }
    return 'No Valid Date';
}

export default function LeadsDrawer({ openState, leadId, parentCallback }: {
    openState: boolean;
    leadId: number;
    parentCallback: (updateInfo?: { id: number }) => void; // MODIFIED: Changed signature to pass back update info
}) {
    const [open, setOpen] = useState(openState);
    const [loadedData, setLoadedData] = useState(false);
    const [user, setUser] = useState<{ username: string }>({ username: '' });
    const [openBookingModal, setOpenBookingModal] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
    // 2. Add state to manage the callback reminder modal
    const [isCallbackModalOpen, setIsCallbackModalOpen] = useState(false);
    const [tab, setTab] = useState(0);
    const initialCustomerState: Customer = {
        advertisement_platform: undefined, created_at: "", email: "", full_name: "",
        house_type: { name: 'Detached', id: 1 }, id: 0, notes: "", phone: "",
        postcode: "", street_address: "",
        roof: { id: 0, confirmed: false, square_meter: 0, roof_url: '', face_count: 0 },
        has_conservatory: false,
        has_detached_garage: false,
        has_attached_garage: false
    };
    const [customer, setCustomer] = useState<Customer>(initialCustomerState);
    const initialLeadState: Lead = {
        created_at: "", customer: initialCustomerState, id: -1, notes: undefined,
        paid: false, services: [], service_price: [], status: [],
        solar_panels: 0, decking_si: 0, patio_si: 0, driveway_si: 0, days_to_complete: 1,
        quote_status: 'preparing'
    };
    const [lead, setLead] = useState<Lead>(initialLeadState);
    const [services, setServices] = useState<AppServiceState>({ ...INITIAL_SERVICES });
    const [roofSquareMeter, setRoofSquareMeter] = useState<string>('');
    const [sentRoofQuote, setSentRoofQuote] = useState<boolean>(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [roofQuoteError, setRoofQuoteError] = useState<string | null>(null);
    // Pre-booking questions - must all be answered before converting to booking
    const [preBookingAnswers, setPreBookingAnswers] = useState<PreBookingAnswers>(createEmptyPreBookingAnswers());
    // Flipped on the first time the user clicks "Convert to Booking" with missing answers,
    // so that unanswered questions are highlighted in red.
    const [showPreBookingValidation, setShowPreBookingValidation] = useState(false);
    const [latestSuggestedRoofPrice, setLatestSuggestedRoofPrice] = useState<number | null>(null);
    const [roofPriceManuallyEdited, setRoofPriceManuallyEdited] = useState(false);
    const [roofPinConfirmationState, setRoofPinConfirmationState] = useState<RoofPinConfirmationState>({
        isActive: false,
        canConfirm: false,
        isLoading: false,
        pinWasMoved: false,
    });
    const roofQuoteTabRef = useRef<ConfirmCustomersRoofTabHandle | null>(null);
    const lastPersistedPreBookingPayload = useRef<string>('');
    const hasTableRefreshChanges = useRef(false);

    const [daysRange, setDaysRange] = useState<{ min: number; max: number } | null>(null);

    const franchiseId = customer.franchise_postcode?.franchise?.id ?? null;
    const faceCount = customer.roof?.face_count || 0;

    useEffect(() => {
        if (!franchiseId) { setDaysRange(null); return; }
        let cancelled = false;
        GetFranchiseVans(franchiseId).then((res: any) => {
            if (cancelled) return;
            const vans: any[] = res?.data ?? [];
            if (vans.length === 0) { setDaysRange(null); return; }
            const daysValues = vans.map((van: any) => {
                const modifier = van.roof_days_sides_modifier > 0 ? van.roof_days_sides_modifier : 2;
                const minimum = van.roof_days_minimum > 0 ? van.roof_days_minimum : 1;
                const calculated = faceCount > 0 ? Math.ceil(faceCount / modifier) : 0;
                return Math.max(minimum, calculated);
            });
            setDaysRange({ min: Math.min(...daysValues), max: Math.max(...daysValues) });
        }).catch(() => { if (!cancelled) setDaysRange(null); });
        return () => { cancelled = true; };
    }, [franchiseId, faceCount]);

    const totalPrice = Object.values(services).reduce((sum, service) => sum + (service.selected ? Number(service.price) || 0 : 0), 0);

    const persistPreBookingAnswers = (answers: PreBookingAnswers) => {
        if (leadId <= 0) return;
        const payload = preBookingAnswersToPayload(answers);
        const payloadKey = `${leadId}:${JSON.stringify(payload)}`;
        if (payloadKey === lastPersistedPreBookingPayload.current) return;
        lastPersistedPreBookingPayload.current = payloadKey;
        hasTableRefreshChanges.current = true;
        setLead(prevLead => ({ ...prevLead, ...payload }));
        ApiService.updateLeadPreBookingAnswers(leadId, payload).catch((error: any) => {
            lastPersistedPreBookingPayload.current = '';
            console.error("Failed to auto-save pre-booking answers:", error);
            toast.error("Failed to auto-save pre-booking answers.", { containerId: "leads-drawer", autoClose: 3000 });
        });
    };

    const handleRoofUpdated = (roof: Customer['roof']) => {
        hasTableRefreshChanges.current = true;
        setCustomer(prevCustomer => ({ ...prevCustomer, roof }));
        setLead(prevLead => ({
            ...prevLead,
            customer: {
                ...prevLead.customer,
                roof,
            },
        }));
    };

    useEffect(() => {
        if (!open || !loadedData || leadId <= 0) return;
        persistPreBookingAnswers(preBookingAnswers);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preBookingAnswers, open, loadedData, leadId]);

    useEffect(() => {
        const fetchUserData = async () => {
            if (open) {
                try {
                    const userResponse = await GetUser();
                    if (userResponse?.data) setUser(userResponse.data);
                } catch (error) {
                    console.error("Failed to fetch user data", error);
                }
            }
        };
        fetchUserData();
    }, [open]);

    useEffect(() => {
        setLead(prevLead => ({ ...prevLead, customer: customer }));
    }, [customer]);

    useEffect(() => {
        if (!open) {
            setLoadedData(false);
            lastPersistedPreBookingPayload.current = '';
            hasTableRefreshChanges.current = false;
            return;
        }
        if (leadId === 0) {
            setLoadedData(true);
            setLead(initialLeadState);
            setCustomer(initialCustomerState);
            setServices({ ...INITIAL_SERVICES });
            setRoofSquareMeter('');
            setSentRoofQuote(false);
            setRoofQuoteError(null);
            setPreBookingAnswers(createEmptyPreBookingAnswers());
            setShowPreBookingValidation(false);
            setLatestSuggestedRoofPrice(null);
            setRoofPriceManuallyEdited(false);
            return;
        }
        setLoadedData(false);
        const fetchData = async () => {
            const getInitializedServices = (basePrices: any, franchisePrices: any, currentLeadData?: Lead): AppServiceState => {
                const newServicesState: AppServiceState = { ...INITIAL_SERVICES };
                for (const key in newServicesState) {
                    const serviceKey = key as keyof AppServiceState;
                    const serviceName = newServicesState[serviceKey].name;
                    const defaultPrice = franchisePrices[`${serviceKey}_price`] ?? basePrices[`${serviceKey}Price`] ?? newServicesState[serviceKey].defaultPrice;
                    newServicesState[serviceKey].defaultPrice = defaultPrice;
                    if (currentLeadData) {
                        const isSelected = currentLeadData.services.some((s: any) => s.name === serviceName);
                        newServicesState[serviceKey].selected = isSelected;
                        if (isSelected) {
                            const leadPriceObj = currentLeadData.service_price?.find((item: any) => item.service.name === serviceName);
                            newServicesState[serviceKey].price = leadPriceObj?.price ?? defaultPrice;
                        } else {
                            newServicesState[serviceKey].price = 0;
                        }
                    }
                }
                return newServicesState;
            };

            try {
                if (leadId === -1) {
                    setCustomer(initialCustomerState);
                    setLead(initialLeadState);
                    setServices({ ...INITIAL_SERVICES });
                } else {
                    const fetchedLead = await ApiService.getLeadById(leadId);
                    if (fetchedLead) {
                        const fetchedCustomer = fetchedLead.customer || initialCustomerState;
                        if (fetchedCustomer.notes == null) fetchedCustomer.notes = '';
                        setCustomer(fetchedCustomer);
                        setLead({ ...initialLeadState, ...fetchedLead });

                        const houseTypeChar = fetchedCustomer.house_type?.name?.charAt(0)?.toUpperCase() || 'D';
                        const houseTypeName = fetchedCustomer.house_type?.name || 'Detached';
                        const servicePriceTiers = await getServicePrices(houseTypeChar, houseTypeName);
                        const basePrices = servicePriceTiers[0] || {};
                        const franchisePrices = fetchedCustomer.franchise_postcode?.service_price_tier || {};

                        setServices(getInitializedServices(basePrices, franchisePrices, fetchedLead));
                        setRoofSquareMeter(fetchedCustomer.roof?.square_meter?.toString() ?? '');
                        setSentRoofQuote(fetchedLead.sent_roof_quote ?? false);
                        setRoofQuoteError(null);
                        // Restore any previously saved pre-booking answers from the lead;
                        // unanswered fields (null) stay unanswered.
                        const restoredAnswers = preBookingAnswersFromLead(fetchedLead);
                        setPreBookingAnswers(restoredAnswers);
                        const shouldPersistDefaults =
                            fetchedLead.pre_booking_garage_included === null ||
                            fetchedLead.pre_booking_conservatory_included === null;
                        if (shouldPersistDefaults) {
                            persistPreBookingAnswers(restoredAnswers);
                        } else {
                            lastPersistedPreBookingPayload.current = `${leadId}:${JSON.stringify(preBookingAnswersToPayload(restoredAnswers))}`;
                            hasTableRefreshChanges.current = false;
                        }
                        setShowPreBookingValidation(false);
                        setLatestSuggestedRoofPrice(null);
                        setRoofPriceManuallyEdited(false);
                    }
                }
            } catch (error) {
                console.error("Failed to fetch lead data", error);
            } finally {
                setLoadedData(true);
            }
        };
        fetchData();
    }, [open, leadId]);

    const setStatus = (newStatus: string) => {
        setLead(prevLead => ({
            ...prevLead,
            status: [...(prevLead.status || []), {
                id: -1, status: newStatus, created_at: new Date().toISOString(), user: user.username
            }]
        }));
    };

    const updateLeadNotes = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setLead(prevLead => ({ ...prevLead, notes: e.target.value }));
    };

    // 3. Update the function to simply open the modal
    const createCallbackAction = () => {
        if (leadId > 0) {
            setIsCallbackModalOpen(true);
        } else {
            toast.error("You must save the lead before creating a reminder.", { theme: "dark" });
        }
    };

    // 4. Create the callback handler for the new modal
    const handleCallbackModalClose = (success: boolean, message: string) => {
        setIsCallbackModalOpen(false); // Always close the modal
        if (message) { // Only show a toast notification if a message is provided
            if (success) {
                toast.success(message, { theme: "dark" });
            } else {
                toast.error(message, { theme: "dark" });
            }
        }
    };

    const getServicesForSave = async (): Promise<AppServiceState> => {
        if (!services.roofClean?.selected || roofPriceManuallyEdited) {
            return services;
        }

        let suggestedPrice = latestSuggestedRoofPrice;
        try {
            const preview = await ApiService.calculateRoofPricePreview({
                customer_id: customer.id || lead.customer?.id,
                square_meter: Number(roofSquareMeter || customer.roof?.square_meter || 0),
                face_count: customer.roof?.face_count || 0,
                conservatory_included: preBookingAnswers.conservatoryIncluded === 'yes',
                conservatory_size: preBookingAnswers.conservatorySize,
                garage_included: preBookingAnswers.garageIncluded === 'yes',
                garage_size: preBookingAnswers.garageSize,
                stories: preBookingAnswers.stories,
                days_to_complete: daysRange?.min ?? lead.days_to_complete ?? 1,
                price_multiplier: customer.franchise_postcode?.affluence_multiplier ?? 1.0,
            });
            if (typeof preview?.recommended_price === 'number') {
                suggestedPrice = preview.recommended_price;
                setLatestSuggestedRoofPrice(preview.recommended_price);
            }
        } catch (error) {
            // If the preview endpoint is unavailable, use the UI-synced value.
        }

        if (typeof suggestedPrice !== 'number') {
            return services;
        }

        const nextServices = {
            ...services,
            roofClean: {
                ...services.roofClean,
                price: suggestedPrice,
            },
        };
        setServices(nextServices);
        return nextServices;
    };

    const convertToBooking = async () => {
        const blockMessage = getBookingBlockMessage(preBookingAnswers);
        if (blockMessage) {
            toast.error(blockMessage, { containerId: "leads-drawer", autoClose: 6000 });
            setTab(1);
            return;
        }
        const missing = validatePreBookingAnswers(preBookingAnswers);
        if (missing.length > 0) {
            setShowPreBookingValidation(true);
            toast.error(
                `Please answer all pre-booking questions before converting: ${missing.join(', ')}`,
                { containerId: "leads-drawer", autoClose: 5000 }
            );
            setTab(1);
            return;
        }
        setShowPreBookingValidation(false);
        try {
            const servicesForSave = await getServicesForSave();
            const payload = {
                ...prepareLeadData(lead, servicesForSave),
                ...preBookingAnswersToPayload(preBookingAnswers),
            };
            if (lead.id > 0) {
                await ApiService.updateLead(leadId, payload);
            } else {
                const createdLead = await ApiService.createLead(payload);
                if (createdLead?.id) {
                    setLead(createdLead);
                } else {
                    throw new Error("Failed to create lead before converting.");
                }
            }
            setOpen(false);
            setOpenBookingModal(true);
        } catch (error) {
            console.error("Error during lead save/create before converting:", error);
        }
    };

    const closeModal = async () => {
        setOpen(false);
        if (leadId > 0 && hasTableRefreshChanges.current) {
            try {
                const servicesForSave = await getServicesForSave();
                const payload = {
                    ...prepareLeadData(lead, servicesForSave),
                    ...preBookingAnswersToPayload(preBookingAnswers),
                };
                await ApiService.updateLead(leadId, payload);
                hasTableRefreshChanges.current = false;
                parentCallback({ id: leadId });
                return;
            } catch (error) {
                console.error("Failed to save auto-updated lead before closing:", error);
                toast.error("Failed to update lead totals before closing.", { containerId: "leads-drawer", autoClose: 3000 });
            }
        }
        parentCallback();
    };

    const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        try {
            const servicesForSave = await getServicesForSave();
            const payload = {
                ...prepareLeadData(lead, servicesForSave),
                ...preBookingAnswersToPayload(preBookingAnswers),
            };
            if (lead.id > 0) {
                await ApiService.updateLead(leadId, payload);
                toast.success("Lead saved successfully!", { containerId: "leads-drawer" });
                hasTableRefreshChanges.current = false;
                setOpen(false);
                parentCallback({ id: leadId });
            } else {
                const newLead = await ApiService.createLead(payload);
                toast.success("New lead created successfully!", { containerId: "leads-drawer" });
                setOpen(false);
                // If a new lead is created, we need to pass its new ID back for potential highlighting
                if (newLead && newLead.id) {
                    parentCallback({ id: newLead.id });
                } else {
                    // if API doesn't return the new lead, we can't highlight.
                    // A generic refetch is still desired. The parent can handle id: -1 as a generic refetch signal
                    parentCallback({ id: -1 });
                }
            }
        } catch (error) {
            console.error("Failed to save lead:", error);
            toast.error("Failed to save lead. Please try again.", { containerId: "leads-drawer" });
        }
    };

    const handleServiceToggle = (serviceKey: keyof AppServiceState) => {
        setServices(prev => {
            const serviceToToggle = prev[serviceKey];
            if (!serviceToToggle) return prev;
            const isNowSelected = !serviceToToggle.selected;
            const updatedService = { ...serviceToToggle, selected: isNowSelected, price: isNowSelected ? serviceToToggle.defaultPrice : 0 };
            const newState = { ...prev, [serviceKey]: updatedService };
            if (!isNowSelected) {
                if (serviceKey === 'drivewayWash') setLead(l => ({ ...l, driveway_si: 0 }));
                else if (serviceKey === 'patioWash') setLead(l => ({ ...l, patio_si: 0 }));
                else if (serviceKey === 'deckingWash') setLead(l => ({ ...l, decking_si: 0 }));
            }
            return newState;
        });
    };

    useEffect(() => {
        if (services.solarPanelClean?.selected) {
            const panelCount = Number(lead.solar_panels) || 0;
            const calculatedPrice = panelCount > 0 ? (9 * panelCount) : 0;
            if (services.solarPanelClean.price !== calculatedPrice) {
                setServices(prev => ({ ...prev, solarPanelClean: { ...prev.solarPanelClean, price: calculatedPrice } }));
            }
        }
    }, [lead.solar_panels, services.solarPanelClean?.selected]);

    const handlePriceChange = (
        serviceKey: keyof AppServiceState,
        value: string,
        source: 'auto' | 'suggested' | 'manual' = 'manual',
    ) => {
        const price = parseFloat(value) || 0;
        if (serviceKey === 'roofClean') {
            setRoofPriceManuallyEdited(source === 'manual');
            if (source !== 'auto') {
                hasTableRefreshChanges.current = true;
            }
        }
        setServices(prev => ({ ...prev, [serviceKey]: { ...prev[serviceKey], price: price } }));
    };

    const renderServiceRow = (serviceKey: keyof AppServiceState) => {
        const service = services[serviceKey];
        if (!service) return null;
        // Only show "preparing" state for existing leads (leadId > 0) that are actually preparing
        const isQuotePreparing = leadId > 0 && lead.quote_status === 'preparing';
        const isRoofQuoteDisabled = !services.roofClean?.selected || leadId <= 0 || isQuotePreparing;
        const roofQuoteTooltip = leadId <= 0 
            ? "Please save this lead before viewing the quote" 
            : isQuotePreparing 
                ? "Quote is being prepared, please wait..." 
                : "";
        return (<ListItem key={String(serviceKey)} variant="outlined" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, width: '100%', boxShadow: 'sm', mb: 1, px: 2, py: 1.5, borderRadius: 'md', }}> <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, gap: 1.5 }}> <ListItemDecorator>{service.icon}</ListItemDecorator> <Typography level="body-md">{service.label}</Typography> {service.label === 'Roof Clean' && isQuotePreparing && (<CircularProgress size="sm" sx={{ ml: 1 }} />)} </Box> {service.label !== 'Roof Clean' ? (<Input startDecorator="£" type="number" value={service.price.toString()} onChange={(e) => handlePriceChange(serviceKey, e.target.value)} disabled={serviceKey === 'solarPanelClean'} slotProps={{ input: { min: 0, step: '0.01' } }} sx={{ maxWidth: 200 }} />) : (<Tooltip title={roofQuoteTooltip} disableHoverListener={!isRoofQuoteDisabled}><span><Button disabled={isRoofQuoteDisabled} onClick={() => setTab(1)}>{isQuotePreparing ? 'Preparing Quote...' : 'View Roof Quote'}</Button></span></Tooltip>)} <IconButton color="danger" onClick={() => handleServiceToggle(serviceKey)}> <DeleteForever /> </IconButton> </ListItem>);
    };

    const handleBookingModalClose = () => {
        setOpenBookingModal(false);
        window.location.reload();
    };

    const handleDeleteCallBack = async (doDelete: boolean) => {
        setOpenDeleteModal(false);
        if (doDelete) {
            await ApiService.deleteLeadById(leadId);
            window.location.reload();
        }
    };

    const handleRoofSqmChange = (newSqm: string) => {
        hasTableRefreshChanges.current = true;
        setRoofSquareMeter(newSqm);
        setCustomer(prevCustomer => ({
            ...prevCustomer,
            roof: {
                ...prevCustomer.roof,
                square_meter: parseFloat(newSqm) || 0
            }
        }));
        if (roofQuoteError) setRoofQuoteError(null);
    };
    const handlePreBookingAnswersChange = (next: PreBookingAnswers) => {
        setPreBookingAnswers(next);
    };

    const handleDownloadQuote = async () => {
        if (!lead.customer.roof?.id) return;
        const sqMeterValue = parseFloat(roofSquareMeter);
        const servicesForQuote = await getServicesForSave();
        const finalPrice = Number(servicesForQuote.roofClean?.price || 0);
        if (isNaN(sqMeterValue) || sqMeterValue <= 0 || isNaN(finalPrice) || finalPrice <= 0) {
            setRoofQuoteError("Please enter valid numbers for square meters and price.");
            return;
        }
        setRoofQuoteError(null);
        setIsDownloading(true);
        try {
            const response = await ApiService.downloadCustomerRoofQuote(lead.customer.roof.id, sqMeterValue, finalPrice);
            const blob = new Blob([response.data], { type: response.headers['content-type'] });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const contentDisposition = response.headers['content-disposition'];
            let filename = `Quote for ${lead.customer.full_name}.pdf`;
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                if (filenameMatch?.[1]) filename = filenameMatch[1];
            }
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err: any) {
            const errorMessage = err.response?.data?.error || "Failed to download quote.";
            setRoofQuoteError(errorMessage);
            toast.error(errorMessage, { theme: "dark" });
        } finally {
            setIsDownloading(false);
        }
    };

    const handleSendQuote = async () => {
        if (!lead.customer.roof?.id) return;
        const sqMeterValue = parseFloat(roofSquareMeter);
        const servicesForQuote = await getServicesForSave();
        const finalPrice = Number(servicesForQuote.roofClean?.price || 0);
        if (isNaN(sqMeterValue) || sqMeterValue <= 0 || isNaN(finalPrice) || finalPrice <= 0) {
            setRoofQuoteError("Please enter valid numbers for square meters and price.");
            return;
        }
        setRoofQuoteError(null);
        setIsSending(true);
        try {
            await ApiService.confirmCustomerRoof(lead.customer.roof.id, sqMeterValue.toString(), finalPrice);
            toast.success('Sent Customer Quote!', { theme: "dark" });
            setSentRoofQuote(true);
        } catch (err: any) {
            const errorMessage = err.response?.data?.error || "Failed to send quote.";
            setRoofQuoteError(errorMessage);
            toast.error(errorMessage, { theme: "dark" });
        } finally {
            setIsSending(false);
        }
    };

    const showSolarPanelInput = services.solarPanelClean?.selected;
    const serviceKeys = Object.keys(services) as Array<keyof AppServiceState>;
    const selectedServiceKeys = serviceKeys.filter(key => services[key]?.selected);
    const unselectedServiceKeys = serviceKeys.filter(key => !services[key]?.selected);
    const showPressureWashingSection = services.drivewayWash?.selected || services.patioWash?.selected || services.deckingWash?.selected;

    const renderFooter = () => {
        const blockMessage = getBookingBlockMessage(preBookingAnswers);
        const convertBlocked = blockMessage !== null;
        const renderConvertButton = () => (
            <Tooltip title={blockMessage ?? ''} disableHoverListener={!convertBlocked} arrow placement="top">
                <span>
                    <Button onClick={convertToBooking} color="primary" disabled={convertBlocked}>
                        Convert To Booking
                    </Button>
                </span>
            </Tooltip>
        );
        switch (tab) {
            case 1:
                const isConfirmingRoofPin = roofPinConfirmationState.isActive;
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
                        {/* Roof Quote Actions Row */}
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', width: '100%' }}>
                            {isConfirmingRoofPin ? (
                                <Button
                                    variant="solid"
                                    color="primary"
                                    disabled={!roofPinConfirmationState.canConfirm}
                                    loading={roofPinConfirmationState.isLoading}
                                    onClick={() => roofQuoteTabRef.current?.openConfirmPinModal()}
                                >
                                    Confirm Location
                                </Button>
                            ) : (
                                <>
                                    <Button variant="solid" color="primary" onClick={handleDownloadQuote} loading={isDownloading}>
                                        Download Quote
                                    </Button>
                                    <Button variant="solid" color="success" onClick={handleSendQuote} loading={isSending}>
                                        {sentRoofQuote ? 'Re-send Quote' : 'Send Quote'}
                                    </Button>
                                </>
                            )}
                        </Box>

                        {/* Standard Lead Actions Row */}
                        <Box sx={{
                            display: 'flex',
                            gap: 1,
                            justifyContent: 'space-between',
                            width: '100%',
                            pt: 2,
                            borderTop: '1px solid',
                            borderColor: 'divider'
                        }}>
                            {leadId > 0 && (
                                <Button
                                    color="danger"
                                    startDecorator={<DeleteForever />}
                                    onClick={() => setOpenDeleteModal(true)}
                                >
                                    Delete
                                </Button>
                            )}
                            <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                                <Dropdown>
                                    <MenuButton color="primary" endDecorator={<ArrowDropDown />}>
                                        Change Lead Status
                                    </MenuButton>
                                    <Menu sx={{ zIndex: 9001 }}>
                                        <MenuItem onClick={() => setStatus('Opt Out')}>Opt Out</MenuItem>
                                    </Menu>
                                </Dropdown>
                                <Button onClick={createCallbackAction} color="success">
                                    Create Callback Reminder
                                </Button>
                                <Button color="success" type="submit" form="lead-modal-form">
                                    {leadId > 0 ? 'Save Lead' : 'Create New Lead'}
                                </Button>
                                {renderConvertButton()}
                            </Box>
                        </Box>
                    </Box>
                );
            case 0:
            default:
                return (
                    <>
                        {leadId > 0 && (
                            <Button
                                color="danger"
                                startDecorator={<DeleteForever />}
                                onClick={() => setOpenDeleteModal(true)}
                                sx={{ mr: 'auto' }}
                            >
                                Delete
                            </Button>
                        )}
                        <Dropdown>
                            <MenuButton color="primary" endDecorator={<ArrowDropDown />}>
                                Change Lead Status
                            </MenuButton>
                            <Menu sx={{ zIndex: 9001 }}>
                                <MenuItem onClick={() => setStatus('Opt Out')}>Opt Out</MenuItem>
                            </Menu>
                        </Dropdown>
                        <Button onClick={createCallbackAction} color="success">
                            Create Callback Reminder
                        </Button>
                        <Button color="success" type="submit" form="lead-modal-form">
                            {leadId > 0 ? 'Save Lead' : 'Create New Lead'}
                        </Button>
                        {renderConvertButton()}
                    </>
                );
        }
    };

    return (
        // The outer <> is the React.Fragment requested
        <>
            <ToastContainer containerId={"leads-drawer"} position="top-right" autoClose={3000} theme="dark" newestOnTop closeOnClick pauseOnFocusLoss draggable pauseOnHover />
            <Drawer open={open} onClose={closeModal} anchor="right" slotProps={{ backdrop: { sx: { backdropFilter: 'none', backgroundColor: 'rgb(108 108 108 / 60%)' } }, content: { sx: { top: 'var(--Header-height)', height: 'calc(100vh - var(--Header-height))' } } }} sx={{ '--Drawer-horizontalSize': { xs: '100vw', sm: '85vw', md: '50vw' } }}>
                <DrawerWithStickyFooter footer={renderFooter()}>
                    <ModalClose sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10 }} />
                    <form onSubmit={handleSave} id="lead-modal-form" style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0, flex: 1 }}>
                        <Tabs sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', '& .MuiTabPanel-root': { flex: 1, overflowY: 'auto', minHeight: 0 } }} aria-label="Lead Modal Tabs" value={tab} onChange={(event, newValue) => setTab(newValue as number)}>
                            <TabList sx={{ flexShrink: 0 }}>
                                <Tab value={0}>Lead</Tab>
                                <Tooltip 
                                    title={leadId <= 0 ? "Please save this lead before viewing the quote" : (leadId > 0 && lead.quote_status === 'preparing') ? "Quote is being prepared, please wait..." : ""} 
                                    disableHoverListener={!(!services.roofClean?.selected || leadId <= 0 || (leadId > 0 && lead.quote_status === 'preparing'))}
                                >
                                    <span>
                                        <Tab value={1} disabled={!services.roofClean?.selected || leadId <= 0 || (leadId > 0 && lead.quote_status === 'preparing')}>
                                            {leadId > 0 && lead.quote_status === 'preparing' ? 'Preparing Quote...' : 'Roof Quote'}
                                        </Tab>
                                    </span>
                                </Tooltip>
                                <Tooltip 
                                    title="Please save this lead before viewing the action log" 
                                    disableHoverListener={leadId > 0}
                                >
                                    <span>
                                        <Tab value={2} disabled={leadId <= 0}>Action Log</Tab>
                                    </span>
                                </Tooltip>
                                <Tooltip 
                                    title="Please save this lead before viewing messages" 
                                    disableHoverListener={leadId > 0}
                                >
                                    <span>
                                        <Tab value={3} disabled={leadId <= 0}>Messages</Tab>
                                    </span>
                                </Tooltip>
                            </TabList>
                            <TabPanel value={0}>
                                {!loadedData ? (<Box sx={{ display: 'flex', minHeight: '300px', gap: 2, alignItems: 'center', justifyContent: 'center' }}><CircularProgress size="lg" /></Box>) : (
                                    <>
                                        <FranchiseeContactCard franchise={customer.franchise_postcode?.franchise} />
                                        {lead.referral_badge_label && (
                                            <Box sx={{ mb: '10px', p: 1.5, border: '1px solid', borderColor: 'primary.outlinedBorder', borderRadius: 'sm', bgcolor: 'primary.softBg' }}>
                                                <Chip color="primary" variant="solid" size="sm" sx={{ mb: 0.75 }}>
                                                    {lead.referral_badge_label}
                                                </Chip>
                                                <Typography level="body-sm">
                                                    This lead came through a referral link{lead.referred_by_name ? ` from ${lead.referred_by_name}` : ''}. Apply the referred customer 5% roof-clean discount.
                                                </Typography>
                                            </Box>
                                        )}
                                        <Box sx={{ mb: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 'sm' }}>
                                            <Box>
                                                <Typography level="title-sm">Ronald AI auto-replies</Typography>
                                                <Typography level="body-xs" color="neutral">
                                                    When on, Ronald will automatically reply to customer SMS on this lead.
                                                </Typography>
                                            </Box>
                                            <Switch
                                                size="lg"
                                                checked={lead.ai_responses_enabled !== false}
                                                onChange={(e) => setLead(prev => ({ ...prev, ai_responses_enabled: e.target.checked }))}
                                            />
                                        </Box>
                                        <CustomersTopComponent customer={customer} setCustomer={setCustomer} />
                                        {showSolarPanelInput && (<Box sx={{ mt: '10px' }}><Typography variant="soft" sx={{ mb: '5px', width: '100%' }}>Solar Panels</Typography><Input startDecorator={<SolarPowerOutlined />} type="number" value={lead.solar_panels || 0} onChange={(e) => { const val = parseInt(e.target.value, 10); setLead(prev => ({ ...prev, solar_panels: isNaN(val) || val < 0 ? 0 : val })); }} sx={{ width: '100%', background: 'white' }} placeholder="Number of Solar Panels" slotProps={{ input: { min: 0 } }} /></Box>)}
                                        {showPressureWashingSection && (<Box sx={{ mt: '10px' }}><Typography variant="soft" sx={{ mb: '5px', width: '100%' }}>Pressure Washing Details (sqm²)</Typography>{services.drivewayWash?.selected && (<Box sx={{ mb: 1 }}><Typography sx={{ mb: 0.5 }}>Driveway sqm²</Typography><Input type="number" value={lead.driveway_si || 0} onChange={(e) => { const v = parseFloat(e.target.value); setLead(p => ({ ...p, driveway_si: isNaN(v) || v < 0 ? 0 : v })); }} sx={{ width: '100%', background: 'white' }} placeholder="Driveway area" endAdornment="sqm²" slotProps={{ input: { min: 0, step: "0.1" } }} /></Box>)}{services.patioWash?.selected && (<Box sx={{ mb: 1 }}><Typography sx={{ mb: 0.5 }}>Patio sqm²</Typography><Input type="number" value={lead.patio_si || 0} onChange={(e) => { const v = parseFloat(e.target.value); setLead(p => ({ ...p, patio_si: isNaN(v) || v < 0 ? 0 : v })); }} sx={{ width: '100%', background: 'white' }} placeholder="Patio area" endAdornment="sqm²" slotProps={{ input: { min: 0, step: "0.1" } }} /></Box>)}{services.deckingWash?.selected && (<Box sx={{ mb: 1 }}><Typography sx={{ mb: 0.5 }}>Decking sqm²</Typography><Input type="number" value={lead.decking_si || 0} onChange={(e) => { const v = parseFloat(e.target.value); setLead(p => ({ ...p, decking_si: isNaN(v) || v < 0 ? 0 : v })); }} sx={{ width: '100%', background: 'white' }} placeholder="Decking area" endAdornment="sqm²" slotProps={{ input: { min: 0, step: "0.1" } }} /></Box>)}</Box>)}
                                        <Box sx={{ mt: '10px' }}><Typography variant="soft" sx={{ mb: '5px', width: '100%' }}>Lead Notes</Typography><Textarea name="notes" value={lead.notes || ''} onChange={updateLeadNotes} variant="outlined" minRows={3} maxRows={3} /></Box>
                                        <Box sx={{ mt: '10px' }}><Typography variant="soft" sx={{ mb: '5px', width: '100%' }}>Past Updates</Typography><Box sx={{ border: '1px solid #CDD7E1', borderRadius: '6px', maxHeight: '150px', overflowY: 'scroll', padding: '10px' }}>{lead.status?.length > 0 ? (lead.status.slice().reverse().map((leadStatus, index) => (<div key={index} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: index === lead.status.length - 1 ? 'none' : '1px dashed #eee' }}><Typography level="body-sm" sx={{ flex: 2 }}>{formatDate(leadStatus.created_at)}</Typography><Typography level="body-sm" sx={{ flex: 1, textAlign: 'center' }}>{leadStatus.status}</Typography><Typography level="body-sm" sx={{ flex: 1, textAlign: 'right' }}>{leadStatus.user}</Typography></div>))) : (<Typography>No Status Updates Found</Typography>)}</Box></Box>
                                        <Box sx={{ mt: '10px' }}><Box sx={{ display: 'flex', flexDirection: 'column' }}><Typography variant="soft">Selected Services</Typography></Box>{selectedServiceKeys.length > 0 ? (<Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}><List sx={{ width: '100%' }}>{selectedServiceKeys.map((serviceKey) => renderServiceRow(serviceKey))}</List></Box>) : (<Box sx={{ display: 'flex', mb: '10px', justifyContent: 'center', alignItems: 'center', p: 3, border: '1px dashed', borderColor: 'divider', borderRadius: 'sm', mt: 1 }}><Typography level="body-sm">No services selected. Click "Add Service" to begin.</Typography></Box>)}</Box>
                                        <Box sx={{ width: '100%', display: 'flex' }}><Dropdown sx={{ width: '100%' }}><MenuButton sx={{ width: '100%' }} size="sm" color="primary" startDecorator={<Add />}>Add Service</MenuButton><Menu sx={{ zIndex: 9001 }}>{unselectedServiceKeys.map((key) => { const service = services[key]; if (!service) return null; return (<MenuItem key={key} onClick={() => handleServiceToggle(key)}><ListItemDecorator>{service.icon}</ListItemDecorator>{service.label}</MenuItem>);})}{unselectedServiceKeys.length === 0 && (<MenuItem disabled>All services have been added</MenuItem>)}</Menu></Dropdown></Box>
                                        <Box sx={{ width: '100%', mb: '5px', display: 'flex', justifyContent: 'flex-end' }}><Typography level="title-md" sx={{ mt: 1 }}>Total Price: £{totalPrice.toFixed(2)}</Typography></Box>
                                    </>
                                )}
                            </TabPanel>
                            <TabPanel value={1}>
                                <FranchiseeContactCard franchise={customer.franchise_postcode?.franchise} />
                                {leadId > 0 && <ConfirmCustomersRoofTab 
                                    ref={roofQuoteTabRef}
                                    key={'confirm-roof-modal-' + lead.customer.id} 
                                    roofData={lead.customer.roof} 
                                    customerData={lead.customer} 
                                    currentPrice={services.roofClean?.price || 0} 
                                    quoteSent={sentRoofQuote}
                                    roofPriceManuallyEdited={roofPriceManuallyEdited}
                                    onPriceChange={(newPrice, source) => handlePriceChange('roofClean', newPrice, source)} 
                                    onSuggestedPriceChange={setLatestSuggestedRoofPrice}
                                    onRoofUpdated={handleRoofUpdated}
                                    onPinConfirmationStateChange={setRoofPinConfirmationState}
                                    squareMeter={roofSquareMeter} 
                                    daysRange={daysRange}
                                    onSquareMeterChange={handleRoofSqmChange} 
                                    answers={preBookingAnswers}
                                    onAnswersChange={handlePreBookingAnswersChange}
                                    onPersistAnswers={persistPreBookingAnswers}
                                    showValidation={showPreBookingValidation}
                                    error={roofQuoteError} 
                                    referralDiscountPercent={lead.referral_discount_percent}
                                />}
                            </TabPanel>
                            <TabPanel value={2} style={{ height: '100%' }}>
                                {leadId > 0 && <Box sx={{ height: '100%', width: '100%' }}><LeadActivityFlow lead_id={leadId} /></Box>}
                            </TabPanel>
                            <TabPanel value={3} style={{ height: '100%' }}>
                                {leadId > 0 && lead.customer && <CustomerMessagesTab customerId={lead.customer.id} />}
                            </TabPanel>
                        </Tabs>
                    </form>
                </DrawerWithStickyFooter>
            </Drawer>
            <ConfirmDeleteModal discardMessage={"Are you sure you want to delete this lead?"} openState={openDeleteModal} parentCallback={handleDeleteCallBack} />
            {openBookingModal && (() => {
                const daysToComplete = daysRange?.min ?? lead.days_to_complete ?? 1;
                const combinedNotes = composeBookingNotes(lead.notes || '', preBookingAnswers, daysToComplete);

                return (
                    <BookingsDrawer
                        key={'bookings-modal-from-lead-' + lead.id}
                        openState={openBookingModal}
                        newBooking={{
                            ...lead,
                            id: -1,
                            booked_in: '',
                            notes: combinedNotes,
                            days_to_complete: daysRange?.min ?? lead.days_to_complete,
                            pre_booking_stories: preBookingAnswers.stories ?? 2,
                        } as any}
                        newBookingValue={services}
                        bookingId={-1}
                        parentCallback={handleBookingModalClose}
                    />
                );
            })()}

            {isCallbackModalOpen && (
                <CreateCallbackReminderModal
                    openState={isCallbackModalOpen}
                    parentCallback={handleCallbackModalClose}
                    lead_id={leadId > 0 ? leadId : null}
                />
            )}
        </>
    );
}